import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';

import type { DashboardWidgetComparisonType } from 'src/constants/dashboard';
import type { components } from 'src/types/allApiTypes.gen';
import type { DashboardWidgetProps, DataSeriesType } from 'src/types/dashboard';
import { numberOrDash } from 'src/helper/helper';
import useGuidedTour from 'src/components/app/context/GuidedTourContext';
import SpeedometerChart from 'src/components/dashboard/charts/SpeedometerChart';
import { useCrossSurveyBenchmark } from 'src/components/dashboard/DashboardContext/CrossSurveyBenchmarkContext';
import { useStatistics } from 'src/components/dashboard/DashboardContext/StatisticsContext';
import NumberDifference from 'src/components/dashboard/DashboardPage/components/NumberDifference';
import WidgetGroupItemFrame from 'src/components/dashboard/DashboardPage/components/WidgetGroupItemFrame';
import { useSpeedometerReData } from 'src/components/dashboard/DashboardPage/widgets/SpeedometerReWidget/useSpeedometerReData';
import useGridSize from 'src/components/dashboard/hooks/useGridSize';
import useQuestionData from 'src/components/dashboard/hooks/useQuestionData';

import SpeedometerReSkeleton from './SpeedometerReSkeleton';

import { styles } from './index.styles';

export const CROSS_SURVEY_BENCHMARK_TEST_ID = 'cross-survey-benchmark';

interface SpeedometerReSubWidgetProps {
  comparisonType: DashboardWidgetComparisonType;
  question: string;
  benchmarkQuestion?: string;
  size?: components['schemas']['SizeEnum'];
  dataseries?: { content?: DataSeriesType };
}

export const SPEEDOMETER_VALUE_TEST_ID = 'speedometer-value';
export const SPEEDOMETER_YOY_DIFF_VALUE_TEST_ID = 'speedometer-yoyDiffValue';
export const SPEEDOMETER_COMPARISON_VALUE_TEST_ID =
  'speedometer-comparisonValue';

const SpeedometerReSubWidget: FC<SpeedometerReSubWidgetProps> = ({
  comparisonType,
  question,
  benchmarkQuestion,
  size,
  dataseries,
}) => {
  const diagram = dataseries?.content?.diagram?.[0];
  const { loading, errorMsg, refetch, data } = useStatistics();
  const { comparisonDataName, comparisonAggregate, focalAggregate } =
    useQuestionData(comparisonType, question, data);
  const { t } = useTranslation('dashboard');
  const sizeIsMOrLarger = useMemo(
    () => size !== 'XXS' && size !== 'XS',
    [size],
  );

  const { loading: benchmarkLoading, data: benchmarkData } =
    useCrossSurveyBenchmark();

  const {
    totalValue,
    yoyDiffAttribute,
    comparisonAttribute,
    benchmarkResultForWidget,
    benchmarkValue,
    benchmarkColor,
    benchmarkPercentage,
  } = useSpeedometerReData({
    diagram,
    focalAggregate,
    comparisonAggregate,
    benchmarkData,
    benchmarkQuestion,
  });

  const hasBenchmark = !benchmarkLoading && !!benchmarkResultForWidget;

  if (loading || errorMsg) {
    return <SpeedometerReSkeleton errorMsg={errorMsg} refetch={refetch} />;
  }

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{ minHeight: 166, p: 3, height: '100%' }}
    >
      <Typography variant="h5" sx={{ mb: 1, flexGrow: 1 }}>
        {focalAggregate?.label || '--'}
      </Typography>

      {hasBenchmark ? (
        <Stack
          direction="row"
          alignItems="center"
          mb={1}
          data-test-id={CROSS_SURVEY_BENCHMARK_TEST_ID}
        >
          <Box sx={styles.squareElement(benchmarkColor)} />
          <Stack direction="row" spacing={0.5}>
            <Typography variant="body3">
              {benchmarkResultForWidget.label ?? '--'}
            </Typography>
            <Typography variant="body3" sx={styles.benchmarkValue}>
              {numberOrDash(benchmarkValue, !!diagram?.show_percent)}
            </Typography>
          </Stack>
        </Stack>
      ) : null}
      <Box sx={{ flexGrow: 0 }}>
        <SpeedometerChart
          percentage={totalValue ? totalValue / 100 : 0}
          height={sizeIsMOrLarger ? 200 : 150}
          color={diagram?.color}
          benchmarkPercentage={benchmarkPercentage}
          benchmarkColor={benchmarkColor}
        >
          <Typography
            component="span"
            data-test-id={SPEEDOMETER_VALUE_TEST_ID}
            sx={styles.percentageValue(sizeIsMOrLarger)}
            fontWeight={700}
            lineHeight="normal"
          >
            {numberOrDash(totalValue, !!diagram?.show_percent)}
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ justifyContent: 'center' }}
          >
            <NumberDifference
              data-test-id={SPEEDOMETER_YOY_DIFF_VALUE_TEST_ID}
              comparisonDiff={yoyDiffAttribute}
              showArrow
              hidePlusMinus
              centerEmpty
              sx={styles.numberDifference(sizeIsMOrLarger)}
            />
          </Stack>
        </SpeedometerChart>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{ flexWrap: 'wrap', mt: 1, justifyContent: 'center' }}
      >
        <Typography variant="body3">
          {comparisonDataName || t('Labels.Benchmark')}
        </Typography>
        <Typography
          variant="body4"
          data-test-id={SPEEDOMETER_COMPARISON_VALUE_TEST_ID}
          sx={styles.benchmarkValue}
        >
          {numberOrDash(comparisonAttribute, !!diagram?.show_percent)}
        </Typography>
      </Stack>
    </Stack>
  );
};

const SpeedometerReWidget: FC<DashboardWidgetProps> = ({
  config,
  className = '',
}) => {
  const { createStep } = useGuidedTour();
  const gridSize = useGridSize(config);
  const guidedTour = useMemo(
    () => createStep(config.id, config.guided_tour_step),
    [config.guided_tour_step],
  );

  return (
    <>
      {config.questions.map((question, i) => (
        <WidgetGroupItemFrame
          key={i}
          config={config}
          className={className}
          gridSizeLg={gridSize}
          guidedTour={
            !!guidedTour &&
            i < 1 && <Box sx={{ position: 'absolute' }}>{guidedTour}</Box>
          }
        >
          <SpeedometerReSubWidget
            question={question}
            benchmarkQuestion={config?.benchmark_question}
            comparisonType={config.comparison_type}
            size={config.size}
            dataseries={config.dataseries}
          />
        </WidgetGroupItemFrame>
      ))}
    </>
  );
};

export default SpeedometerReWidget;
